import { useEffect, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  BookOpen,
  Calendar,
  Check,
  CircleCheck,
  CircleX,
  Clapperboard,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff,
  Instagram,
  Loader2,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Phone,
  Plus,
  Send,
  ShieldAlert,
  Trash2,
  TriangleAlert,
  Unlock,
  Users,
  Wallet,
  X,
  XCircle,
} from '@/lib/icons'
import type {
  BloggerAssignBookRequest,
  BloggerBlockRequest,
  BloggerDetail as BloggerDetailData,
  BloggerFinance,
  BloggerReviewRequest,
  BloggerVideoSummary,
  BookAssignmentItem,
  BookItem,
  CommentItem,
  RejectReason as RejectReasonType,
  VideoStage,
} from '@ayub/shared'
import {
  BLOGGER_STAGE_LABELS,
  BloggerStage,
  formatNumber,
  formatSom,
  REJECT_REASON_LABELS,
  VIDEO_STAGE_LABELS,
} from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Modal } from '@/components/ui/modal'
import { RejectReasonPicker } from '@/components/ui/reject-reason-picker'
import { Skeleton } from '@/components/ui/skeleton'
import { HistorySection } from '@/components/history/HistorySection'
import { fetchBooks } from '@/components/books/api'
import { ApiError, apiDelete, apiGet, apiPost } from '@/lib/api'
import { formatDateUz, formatRelativeUz } from '@/lib/relative-date'
import { cn } from '@/lib/utils'

export interface BloggerDetailProps {
  /** `null` bo'lsa kartochka yopiq — hech narsa render qilinmaydi (Modal o'zi buni boshqaradi). */
  bloggerId: number | null
  onClose: () => void
  /** Kartochkadagi "Chat" tugmasi — kartochka yopilib, Chat bo'limida shu suhbat ochiladi. */
  onOpenChat?: (bloggerId: number) => void
}

/**
 * `GET /api/bloggers/:id/card` javobi — `packages/shared/src/api.ts` bu endpoint uchun
 * alohida ochiq tip bermaydi (faqat yo'l va "audit qilinadi" talabi hujjatlarda tilga
 * olingan). Shakl `apps/server/src/services/bloggers.ts`dagi `BloggerCard`ga mos yozilgan
 * (server ichki tipi, WebApp'ga import qilib bo'lmaydi — shuning uchun shu yerda ko'chirilgan).
 */
interface CardRevealResponse {
  cardNumber: string
  cardHolder: string | null
}

const STAGE_BADGE_CLASS: Record<BloggerStage, string> = {
  NEW: 'bg-urgency-today/10 text-urgency-today-text',
  ACTIVE: 'bg-urgency-done/10 text-urgency-done-text',
  REJECTED: 'bg-stage-rejected/10 text-stage-rejected-text',
  BLOCKED: 'bg-app-hint/15 text-app-hint',
}

const VIDEO_STAGE_DOT_CLASS: Record<VideoStage, string> = {
  MODERATION: 'bg-stage-moderation',
  POSTED: 'bg-stage-posted',
  CHECKING: 'bg-stage-checking',
  PAID: 'bg-stage-paid',
  REJECTED: 'bg-stage-rejected',
}

/** `+998901234567` → `+998 90 123 45 67`. Formatga mos kelmasa xom qiymat qaytadi. */
function formatPhoneDisplay(phone: string): string {
  const match = /^\+998(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(phone)
  if (!match) return phone
  const [, a, b, c, d] = match
  return `+998 ${a} ${b} ${c} ${d}`
}

/**
 * Bloger kartochkasi — `docs/03-app-flow.md` "Bloger kartochkasi (ochilganda)".
 *
 * Dizayn maqsadi shu hujjatdagi "Yangi arizani ko'rish tartibi" — admin 30 soniyada:
 * ochadi → Instagram havolasini bosadi → ko'z yugurtiradi → qaror qiladi. Shuning uchun
 * Instagram havolasi va qaror tugmalari LAYOUT darajasida kafolatlangan: ular header/footer
 * qismida (flex `shrink-0`), faqat o'rtadagi tarkib (`flex-1 min-h-0 overflow-y-auto`)
 * skrollanadi. Kartochka qanchalik uzun bo'lmasin, bu ikkalasi hech qachon skroll ortida
 * qolmaydi — flexbox buni CSS darajasida ta'minlaydi, JS hisob-kitobsiz.
 */
export function BloggerDetail({ bloggerId, onClose, onOpenChat }: BloggerDetailProps) {
  const queryClient = useQueryClient()

  const [cardRevealed, setCardRevealed] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [confirmKind, setConfirmKind] = useState<'approve' | 'block' | 'unblock' | null>(null)
  const [rejectPickerOpen, setRejectPickerOpen] = useState(false)
  // Tasdiqlash oynasida tanlangan kitob (ixtiyoriy).
  const [approveBookId, setApproveBookId] = useState<number | null>(null)
  // Kartochkadagi "Kitob qo'shish" tanlagichi.
  const [addBookId, setAddBookId] = useState<number | null>(null)
  // Olib tashlash tasdiqlanayotgan biriktirish (null = oyna yopiq).
  const [removeTarget, setRemoveTarget] = useState<BookAssignmentItem | null>(null)

  const detailQuery = useQuery<BloggerDetailData, Error>({
    queryKey: ['blogger', bloggerId],
    queryFn: () => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiGet<BloggerDetailData>(`/bloggers/${bloggerId}`)
    },
    enabled: bloggerId !== null,
  })

  // Kitob katalogi — tasdiqlash oynasidagi va kartochkadagi tanlagichlar uchun. `BooksPage`
  // bilan bir xil kesh kaliti (`['books']`) — ikkalasi bitta ro'yxatdan foydalanadi.
  const booksQuery = useQuery<BookItem[], Error>({
    queryKey: ['books'],
    queryFn: fetchBooks,
    enabled: bloggerId !== null,
  })

  const cardMutation = useMutation<CardRevealResponse, Error, void>({
    mutationFn: () => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiGet<CardRevealResponse>(`/bloggers/${bloggerId}/card`)
    },
  })

  const reviewMutation = useMutation<BloggerDetailData, Error, BloggerReviewRequest>({
    mutationFn: (body) => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiPost<BloggerDetailData>(`/bloggers/${bloggerId}/review`, body)
    },
    onSuccess: (updated) => {
      // Server yangilangan to'liq kartochkani qaytaradi — qo'shimcha so'rovni kutmasdan
      // darhol keshga yozamiz, shunda bosqich belgisi va tugmalar bir zumda yangilanadi.
      queryClient.setQueryData(['blogger', bloggerId], updated)
      invalidateListCaches()
      setConfirmKind(null)
      setRejectPickerOpen(false)
      setApproveBookId(null)
    },
  })

  const blockMutation = useMutation<BloggerDetailData, Error, BloggerBlockRequest>({
    mutationFn: (body) => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiPost<BloggerDetailData>(`/bloggers/${bloggerId}/block`, body)
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['blogger', bloggerId], updated)
      invalidateListCaches()
      setConfirmKind(null)
    },
  })

  const commentMutation = useMutation<CommentItem, Error, string>({
    mutationFn: (text) => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiPost<CommentItem>(`/bloggers/${bloggerId}/comments`, { text })
    },
    onSuccess: () => {
      setCommentText('')
      void queryClient.invalidateQueries({ queryKey: ['blogger', bloggerId] })
    },
  })

  const assignBookMutation = useMutation<BloggerDetailData, Error, BloggerAssignBookRequest>({
    mutationFn: (body) => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiPost<BloggerDetailData>(`/bloggers/${bloggerId}/books`, body)
    },
    onSuccess: (updated) => {
      // Server yangilangan to'liq kartochkani qaytaradi — darhol keshga yozamiz.
      queryClient.setQueryData(['blogger', bloggerId], updated)
      invalidateListCaches()
      setAddBookId(null)
    },
  })

  const removeBookMutation = useMutation<BloggerDetailData, Error, number>({
    mutationFn: (assignmentId) => {
      if (bloggerId === null) throw new Error("Bloger ID yo'q")
      return apiDelete<BloggerDetailData>(`/bloggers/${bloggerId}/books/${assignmentId}`)
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['blogger', bloggerId], updated)
      invalidateListCaches()
      setRemoveTarget(null)
    },
  })

  /**
   * Bitta qaror (tasdiqlash/rad etish/bloklash) blogerlar ro'yxati, kanban va Dashboard
   * hisoblagichlarini eskirtiradi ("Yangi ariza: 4" tasdiqlangandan keyin ham 4 bo'lib
   * qolishi ishonchni yo'qotadi). Shu kartochkaning o'zi `setQueryData` bilan darhol
   * yangilanadi (server `review`/`block`dan to'liq `BloggerDetail` qaytaradi — qo'shimcha
   * so'rov shart emas); ro'yxat/kanban/hisoblagichlar esa invalidate qilinadi — ularning
   * aniq kalit shaklini (`bloggers/hooks.ts`) shu komponent bilmasligi kerak, shuning uchun
   * birinchi segment (`bloggers`, `dashboard`) bo'yicha keng invalidatsiya ishlatiladi.
   */
  function invalidateListCaches() {
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0]
        return key === 'bloggers' || key === 'dashboard'
      },
    })
  }

  // Yangi bloger ochilganda (yoki karta yopilganda) — oldingi holatlar qolib ketmasin.
  // Ayniqsa muhim: oldingi blogerning ochilgan karta raqami hech qachon keyingisiga
  // "ko'chib o'tmasligi" kerak.
  useEffect(() => {
    setCardRevealed(false)
    setCommentText('')
    setConfirmKind(null)
    setRejectPickerOpen(false)
    setApproveBookId(null)
    setAddBookId(null)
    setRemoveTarget(null)
    cardMutation.reset()
    // Faqat `bloggerId` o'zgarganda ishga tushishi kerak — boshqa qiymatlar ataylab
    // dependency ro'yxatida yo'q, aks holda har bir mutatsiya holati o'zgarishida
    // (masalan karta yuklanayotganda) forma tozalanib ketardi.
  }, [bloggerId])

  function handleToggleCard() {
    if (cardRevealed) {
      setCardRevealed(false)
      return
    }
    setCardRevealed(true)
    // Audit qilinadigan amal — faqat birinchi marta ochilganda so'raladi, qayta
    // yashirib-ko'rsatishda takroriy so'rov yuborilmaydi.
    if (!cardMutation.data) {
      cardMutation.mutate()
    }
  }

  function handleApproveConfirm() {
    reviewMutation.mutate({ approved: true, bookId: approveBookId ?? undefined })
  }

  function handleAssignBook() {
    if (addBookId === null) return
    assignBookMutation.mutate({ bookId: addBookId })
  }

  function handleRejectSubmit(reason: RejectReasonType, note: string | undefined) {
    reviewMutation.mutate({ approved: false, reason, note })
  }

  function handleBlockConfirm() {
    blockMutation.mutate({ blocked: true })
  }

  function handleUnblockConfirm() {
    blockMutation.mutate({ blocked: false })
  }

  function handleAddComment(event: FormEvent) {
    event.preventDefault()
    const trimmed = commentText.trim()
    if (trimmed.length === 0) return
    commentMutation.mutate(trimmed)
  }

  const detailError = detailQuery.error
  const cardError = cardMutation.error
  const reviewError = reviewMutation.error
  const blockError = blockMutation.error
  const commentError = commentMutation.error
  const actionErrorMessage = reviewError?.message ?? blockError?.message ?? null

  let confirmTitle = ''
  let confirmDescription: ReactNode = null
  let confirmLabel = ''
  let confirmTone: 'primary' | 'danger' = 'primary'
  let handleConfirmAction: () => void = () => {}

  if (confirmKind === 'approve') {
    confirmTitle = 'Arizani tasdiqlaysizmi?'
    confirmDescription = (
      <div className="flex flex-col gap-3">
        <p>
          {detailQuery.data?.fullName ?? 'Bloger'} hamkor sifatida faollashadi va video yuborishni
          boshlashi mumkin bo&apos;ladi.
        </p>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-app-text">
            Biriktiriladigan kitob <span className="text-stage-rejected-text">*</span>
          </span>
          <BookSelect
            books={booksQuery.data ?? []}
            loading={booksQuery.isLoading}
            value={approveBookId}
            onChange={setApproveBookId}
            disabled={reviewMutation.isPending}
          />
          <span className="text-xs text-app-hint">
            {booksQuery.data && booksQuery.data.length === 0 && !booksQuery.isLoading
              ? 'Katalog bo‘sh — avval "Kitoblar" bo‘limida kitob qo‘shing.'
              : 'Tasdiqlash uchun kitob tanlang — bloger tasdiqlangani va kitob berilgani haqida botdan xabar oladi.'}
          </span>
        </label>
      </div>
    )
    confirmLabel = 'Ha, tasdiqlash'
    confirmTone = 'primary'
    handleConfirmAction = handleApproveConfirm
  } else if (confirmKind === 'block') {
    confirmTitle = 'Blogerni bloklaysizmi?'
    confirmDescription = (
      <>
        Bloklangandan so&apos;ng bloger yangi video yubora olmaydi va botning asosiy funksiyalaridan
        foydalana olmaydi. Joriy balansi va videolari o&apos;zgarmaydi. Buni istalgan vaqt qaytarish
        (blokdan chiqarish) mumkin.
      </>
    )
    confirmLabel = 'Ha, bloklash'
    confirmTone = 'danger'
    handleConfirmAction = handleBlockConfirm
  } else if (confirmKind === 'unblock') {
    confirmTitle = 'Blokdan chiqarasizmi?'
    confirmDescription = (
      <>Bloger yana video yuborishi va bot funksiyalaridan foydalanishi mumkin bo&apos;ladi.</>
    )
    confirmLabel = 'Ha, blokdan chiqarish'
    confirmTone = 'primary'
    handleConfirmAction = handleUnblockConfirm
  }

  return (
    <>
      <Modal
        open={bloggerId !== null}
        onClose={onClose}
        ariaLabel={
          detailQuery.data
            ? `${detailQuery.data.fullName} — bloger kartochkasi`
            : 'Bloger kartochkasi'
        }
      >
        {detailQuery.isLoading && <DetailSkeleton />}

        {!detailQuery.isLoading && detailQuery.isError && (
          <DetailErrorState
            error={detailError}
            onRetry={() => void detailQuery.refetch()}
            onClose={onClose}
          />
        )}

        {!detailQuery.isLoading && detailQuery.data && (
          <div className="flex h-full min-h-0 flex-col">
            <IdentityHeader
              blogger={detailQuery.data}
              onClose={onClose}
              onBlockClick={() => setConfirmKind('block')}
              onUnblockClick={() => setConfirmKind('unblock')}
            />

            {/* `overscroll-contain` — iOS Safari'da bu ichki skroll oxiriga yetganda "rubber-band"
                effekti orqadagi sahifaga (modal orqasidagi body) o'tib ketmasin, aks holda
                `body.style.overflow = 'hidden'` (modal.tsx) yetarli bo'lmaydi. */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="flex flex-col gap-4 p-4">
                <IdentityExtra
                  blogger={detailQuery.data}
                  cardRevealed={cardRevealed}
                  cardNumber={cardMutation.data?.cardNumber}
                  cardHolder={cardMutation.data?.cardHolder}
                  cardLoading={cardMutation.isPending}
                  cardErrorMessage={cardError ? cardError.message : null}
                  onToggleCard={handleToggleCard}
                  onOpenChat={onOpenChat}
                />
                <FinanceSection finance={detailQuery.data.finance} />
                <BooksSection
                  assignments={detailQuery.data.bookAssignments}
                  canAdd={detailQuery.data.stage === BloggerStage.ACTIVE}
                  books={booksQuery.data ?? []}
                  booksLoading={booksQuery.isLoading}
                  addBookId={addBookId}
                  onAddBookIdChange={setAddBookId}
                  onAssign={handleAssignBook}
                  isAssigning={assignBookMutation.isPending}
                  assignErrorMessage={
                    assignBookMutation.error ? assignBookMutation.error.message : null
                  }
                  onRemoveClick={setRemoveTarget}
                />
                <VideosSection videos={detailQuery.data.videos} />
                <CommentsSection
                  comments={detailQuery.data.comments}
                  commentText={commentText}
                  onCommentTextChange={setCommentText}
                  onSubmit={handleAddComment}
                  isSubmitting={commentMutation.isPending}
                  errorMessage={commentError ? commentError.message : null}
                />
                {/* Izohlardan KEYIN, kartochka pastida — vazifa talabi. `entityType='blogger'`,
                    `entityId=bloggerId` (bu yerda `null` emasligi kafolatlangan: bu blok
                    faqat `detailQuery.data` mavjud bo'lganda render qilinadi, u esa
                    `bloggerId !== null`da yoqilgan so'rovdan keladi). */}
                <HistorySection entityType="blogger" entityId={detailQuery.data.id} />
              </div>
            </div>

            <DecisionFooter
              stage={detailQuery.data.stage}
              isPending={reviewMutation.isPending || blockMutation.isPending}
              errorMessage={actionErrorMessage}
              onApproveClick={() => setConfirmKind('approve')}
              onRejectClick={() => setRejectPickerOpen(true)}
              onClose={onClose}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmKind !== null}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        tone={confirmTone}
        isLoading={reviewMutation.isPending || blockMutation.isPending}
        // Tasdiqlash uchun kitob tanlanishi MAJBURIY — tanlanmaguncha tugma o'chiq.
        confirmDisabled={confirmKind === 'approve' && approveBookId === null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmKind(null)}
      />

      <RejectReasonPicker
        open={rejectPickerOpen}
        isSubmitting={reviewMutation.isPending}
        onCancel={() => setRejectPickerOpen(false)}
        onSubmit={handleRejectSubmit}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title="Kitobni olib tashlaysizmi?"
        description={
          <>
            «{removeTarget?.bookTitle}» ushbu blogerdan olib tashlanadi. Amal tarixda
            (o&apos;zgarishlar jurnalida) qoladi.
          </>
        }
        confirmLabel="Ha, olib tashlash"
        tone="danger"
        isLoading={removeBookMutation.isPending}
        errorMessage={removeBookMutation.error ? removeBookMutation.error.message : null}
        onConfirm={() => {
          if (removeTarget) removeBookMutation.mutate(removeTarget.id)
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </>
  )
}

/**
 * Sarlavhadagi "⋯" menyu — Bloklash / Blokdan chiqarish shu yerda. Faqat shu amal mavjud
 * bosqichlarda (ACTIVE → Bloklash, BLOCKED → Blokdan chiqarish) ko'rinadi; NEW/REJECTED'da
 * umuman render qilinmaydi. Tashqariga bosilganda ko'rinmas qatlam orqali yopiladi.
 */
function HeaderMenu({
  stage,
  onBlockClick,
  onUnblockClick,
}: {
  stage: BloggerStage
  onBlockClick: () => void
  onUnblockClick: () => void
}) {
  const [open, setOpen] = useState(false)

  if (stage !== BloggerStage.ACTIVE && stage !== BloggerStage.BLOCKED) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Boshqa amallar"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg"
      >
        <MoreVertical size={20} aria-hidden="true" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 min-w-44 overflow-hidden rounded-card border border-app-border bg-app-card py-1 shadow-lg"
          >
            {stage === BloggerStage.ACTIVE && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onBlockClick()
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-stage-rejected-text transition-colors duration-150 ease-out hover:bg-app-bg"
              >
                <Ban size={16} aria-hidden="true" />
                Bloklash
              </button>
            )}
            {stage === BloggerStage.BLOCKED && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onUnblockClick()
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-app-text transition-colors duration-150 ease-out hover:bg-app-bg"
              >
                <Unlock size={16} aria-hidden="true" />
                Blokdan chiqarish
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

interface IdentityHeaderProps {
  blogger: BloggerDetailData
  onClose: () => void
  onBlockClick: () => void
  onUnblockClick: () => void
}

/**
 * Modalning skroll qilinmaydigan yuqori qismi — ism, holat, va eng muhimi, Instagram
 * havolasi. Shu yerda bo'lgani uchun karta qanchalik uzun bo'lmasin, admin uni doim,
 * hech qanday skrollsiz ko'radi ("30 soniyalik" ish shu havoladan boshlanadi).
 *
 * "⋯" menyu — Bloklash/Blokdan chiqarish shu yerda (footer'dagi asosiy tugma o'rnida EMAS):
 * bu xavfli amal footer'dagi "muskul-xotira" bosiladigan joyda turmasligi kerak (admin uni
 * bilmasdan bosib yubormasin).
 */
function IdentityHeader({ blogger, onClose, onBlockClick, onUnblockClick }: IdentityHeaderProps) {
  const instagramUrl = `https://instagram.com/${blogger.instagramUsername}`

  return (
    <div className="shrink-0 border-b border-app-border p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg leading-tight font-semibold">{blogger.fullName}</h1>
          <span
            className={cn(
              'mt-1 inline-flex items-center rounded-badge px-2 py-0.5 text-xs font-medium',
              STAGE_BADGE_CLASS[blogger.stage],
            )}
          >
            {BLOGGER_STAGE_LABELS[blogger.stage]}
          </span>
        </div>
        <div className="flex shrink-0 items-center">
          <HeaderMenu
            stage={blogger.stage}
            onBlockClick={onBlockClick}
            onUnblockClick={onUnblockClick}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Yopish"
            className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
      </div>

      <a
        href={instagramUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-h-11 items-center justify-between gap-3 rounded-button border border-app-border bg-app-bg px-3 py-2 text-sm font-medium transition-colors duration-150 ease-out hover:border-brand hover:text-brand"
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <Instagram size={16} className="shrink-0 text-app-hint" aria-hidden="true" />
          <span className="truncate">@{blogger.instagramUsername}</span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 text-brand">
          <ExternalLink size={14} aria-hidden="true" />
          Ochish
        </span>
      </a>

      <div className="mt-2 flex items-center gap-1.5 text-sm text-app-hint">
        <Users size={14} aria-hidden="true" />
        {formatNumber(blogger.followers)} obunachi
      </div>
    </div>
  )
}

interface IdentityExtraProps {
  blogger: BloggerDetailData
  cardRevealed: boolean
  cardNumber: string | undefined
  cardHolder: string | null | undefined
  cardLoading: boolean
  cardErrorMessage: string | null
  onToggleCard: () => void
  onOpenChat?: (bloggerId: number) => void
}

/** Telefon, karta raqami (maskalangan/ochiladigan) va qo'shilgan sana — skroll qismining tepasida. */
function IdentityExtra({
  blogger,
  cardRevealed,
  cardNumber,
  cardHolder,
  cardLoading,
  cardErrorMessage,
  onToggleCard,
  onOpenChat,
}: IdentityExtraProps) {
  const telegramUrl = blogger.telegramUsername ? `https://t.me/${blogger.telegramUsername}` : null
  const displayedCard = cardRevealed
    ? (cardNumber ?? blogger.cardNumberMasked ?? "Karta yo'q")
    : (blogger.cardNumberMasked ?? "Karta yo'q")

  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-app-border bg-app-card p-4 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-app-hint">
          <Phone size={14} aria-hidden="true" />
          {formatPhoneDisplay(blogger.phone)}
        </span>
        <div className="flex items-center gap-3">
          {onOpenChat && (
            <button
              type="button"
              onClick={() => onOpenChat(blogger.id)}
              className="inline-flex min-h-11 items-center gap-1 font-medium text-brand"
            >
              <MessageSquare size={14} aria-hidden="true" />
              Chat
            </button>
          )}
          {telegramUrl ? (
            <a
              href={telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-1 font-medium text-app-hint"
            >
              <MessageCircle size={14} aria-hidden="true" />
              Yozish
            </a>
          ) : (
            <span className="text-xs text-app-hint">Telegram username yo&apos;q</span>
          )}
        </div>
      </div>

      {blogger.cardNumberMasked && (
        <div className="flex items-center justify-between gap-3">
          <span className="tabular inline-flex items-center gap-2 text-app-hint">
            <CreditCard size={14} aria-hidden="true" />
            {cardRevealed && cardLoading ? 'Yuklanmoqda...' : displayedCard}
          </span>
          <button
            type="button"
            onClick={onToggleCard}
            className="inline-flex min-h-11 items-center gap-1 text-xs font-medium text-app-hint transition-colors duration-150 ease-out hover:text-brand"
          >
            {cardRevealed ? (
              <EyeOff size={14} aria-hidden="true" />
            ) : (
              <Eye size={14} aria-hidden="true" />
            )}
            {cardRevealed ? 'Yashirish' : "Ko'rish"}
          </button>
        </div>
      )}

      {cardRevealed && cardErrorMessage && (
        <p className="text-xs text-stage-rejected-text">{cardErrorMessage}</p>
      )}
      {cardRevealed && cardNumber && (
        <>
          {cardHolder && <p className="text-xs text-app-hint">Karta egasi: {cardHolder}</p>}
          <p className="inline-flex items-center gap-1.5 rounded-badge bg-urgency-today/10 px-2 py-1 text-xs font-medium text-urgency-today-text">
            <ShieldAlert size={12} aria-hidden="true" />
            Maxfiy ma&apos;lumot — ekranni boshqalarga ko&apos;rsatmang, kerak bo&apos;lmasa
            yashiring.
          </p>
        </>
      )}

      <div className="flex items-center gap-2 text-app-hint">
        <Calendar size={14} aria-hidden="true" />
        Qo&apos;shilgan: {formatDateUz(blogger.createdAt)}
      </div>

      {blogger.botBlocked && (
        <p className="inline-flex items-center gap-1.5 rounded-badge bg-urgency-overdue/10 px-2 py-1 text-xs font-medium text-urgency-overdue-text">
          <TriangleAlert size={12} aria-hidden="true" />
          Bloger botni bloklagan — bildirishnomalar yetib bormaydi
        </p>
      )}

      {blogger.stage === BloggerStage.REJECTED && blogger.rejectReason && (
        <p className="text-xs text-app-hint">
          Rad etish sababi:{' '}
          <span className="font-medium text-app-text">
            {REJECT_REASON_LABELS[blogger.rejectReason]}
          </span>
          {blogger.rejectNote ? ` — ${blogger.rejectNote}` : null}
        </p>
      )}
    </section>
  )
}

function FinanceSection({ finance }: { finance: BloggerFinance }) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Wallet size={16} className="text-brand" aria-hidden="true" />
        Moliya
      </h2>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FinanceStat label="Balans (to'lanmagan)" value={formatSom(finance.pendingAmount)} />
        <FinanceStat
          label="Jarayonda"
          value={formatSom(finance.inProgressAmount)}
          hint="taxminiy"
        />
        <FinanceStat label="Jami to'langan" value={formatSom(finance.paidAmount)} />
      </dl>
    </section>
  )
}

function FinanceStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-app-hint">{label}</dt>
      <dd className="tabular text-base font-semibold text-brand">{value}</dd>
      {hint && <span className="text-xs text-app-hint">{hint}</span>}
    </div>
  )
}

/** Katalogdan kitob tanlash — tasdiqlash oynasida ham, kartochkadagi "qo'shish"da ham ishlatiladi. */
function BookSelect({
  books,
  loading,
  value,
  onChange,
  disabled,
  className,
}: {
  books: BookItem[]
  loading: boolean
  value: number | null
  onChange: (value: number | null) => void
  disabled?: boolean
  className?: string
}) {
  return (
    <select
      value={value ?? ''}
      disabled={disabled || loading}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      className={cn(
        'min-h-11 rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60',
        className,
      )}
    >
      <option value="">{loading ? 'Yuklanmoqda…' : '— Kitob tanlanmagan —'}</option>
      {books.map((book) => (
        <option key={book.id} value={book.id}>
          {book.title} — {formatSom(book.price)}
        </option>
      ))}
    </select>
  )
}

interface BooksSectionProps {
  assignments: BookAssignmentItem[]
  /** Faqat faol (ACTIVE) blogerga kartochkadan kitob qo'shiladi. */
  canAdd: boolean
  books: BookItem[]
  booksLoading: boolean
  addBookId: number | null
  onAddBookIdChange: (value: number | null) => void
  onAssign: () => void
  isAssigning: boolean
  assignErrorMessage: string | null
  onRemoveClick: (assignment: BookAssignmentItem) => void
}

/**
 * Blogerga berilgan kitoblar — "qaysi blogerga nechta kitob berildi (sotildi)". Birinchi
 * kitob odatda tasdiqlashda biriktiriladi; bu yerda ro'yxat ko'rinadi va faol blogerga
 * qo'shimcha kitob qo'shish mumkin.
 */
function BooksSection({
  assignments,
  canAdd,
  books,
  booksLoading,
  addBookId,
  onAddBookIdChange,
  onAssign,
  isAssigning,
  assignErrorMessage,
  onRemoveClick,
}: BooksSectionProps) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <BookOpen size={16} className="text-brand" aria-hidden="true" />
        Berilgan kitoblar ({assignments.length})
      </h2>

      {assignments.length === 0 ? (
        <p className="text-sm text-app-hint">Hali kitob biriktirilmagan</p>
      ) : (
        <ul className="flex flex-col divide-y divide-app-border">
          {assignments.map((assignment) => (
            <li key={assignment.id} className="flex items-start justify-between gap-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate text-app-text">{assignment.bookTitle}</p>
                <p className="tabular mt-0.5 text-xs text-app-hint">
                  {formatSom(assignment.price)} · {formatDateUz(assignment.assignedAt)}
                  {assignment.note ? ` · ${assignment.note}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onRemoveClick(assignment)}
                aria-label="Kitobni olib tashlash"
                className="flex size-9 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-bg hover:text-stage-rejected-text"
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {canAdd &&
        (books.length === 0 && !booksLoading ? (
          <p className="text-xs text-app-hint">
            Katalog bo&apos;sh — avval &quot;Kitoblar&quot; bo&apos;limida kitob qo&apos;shing.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <BookSelect
                books={books}
                loading={booksLoading}
                value={addBookId}
                onChange={onAddBookIdChange}
                disabled={isAssigning}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={onAssign}
                disabled={isAssigning || addBookId === null}
              >
                {isAssigning ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <Plus size={14} aria-hidden="true" />
                )}
                Qo&apos;shish
              </Button>
            </div>
            {assignErrorMessage && (
              <span className="text-xs text-stage-rejected-text">{assignErrorMessage}</span>
            )}
          </div>
        ))}
    </section>
  )
}

function VideosSection({ videos }: { videos: BloggerVideoSummary[] }) {
  return (
    <section className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Clapperboard size={16} className="text-brand" aria-hidden="true" />
        Videolari ({videos.length})
      </h2>
      {videos.length === 0 ? (
        <p className="text-sm text-app-hint">Hali video yubormagan</p>
      ) : (
        <ul className="flex flex-col divide-y divide-app-border">
          {videos.map((video) => (
            <li key={video.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="inline-flex min-w-0 items-center gap-2 text-app-text">
                <span
                  className={cn('size-2 shrink-0 rounded-full', VIDEO_STAGE_DOT_CLASS[video.stage])}
                  aria-hidden="true"
                />
                <span className="truncate">
                  #{video.id} · {VIDEO_STAGE_LABELS[video.stage]}
                </span>
              </span>
              <span className="tabular shrink-0 text-app-hint">
                {video.views !== null ? formatNumber(video.views) : '—'}
                {video.amount !== null ? ` · ${formatSom(video.amount)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface CommentsSectionProps {
  comments: CommentItem[]
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmit: (event: FormEvent) => void
  isSubmitting: boolean
  errorMessage: string | null
}

function CommentsSection({
  comments,
  commentText,
  onCommentTextChange,
  onSubmit,
  isSubmitting,
  errorMessage,
}: CommentsSectionProps) {
  const sorted = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  return (
    <section className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <MessageSquare size={16} className="text-brand" aria-hidden="true" />
        Izohlar
      </h2>

      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <textarea
          value={commentText}
          onChange={(event) => onCommentTextChange(event.target.value)}
          rows={2}
          placeholder="Izoh yozish..."
          className="rounded-input border border-app-border bg-app-bg px-3 py-2 text-base text-app-text outline-none focus-visible:border-brand"
        />
        <div className="flex items-center justify-between gap-2">
          {errorMessage ? (
            <span className="text-xs text-stage-rejected-text">{errorMessage}</span>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={isSubmitting || commentText.trim().length === 0}
          >
            {isSubmitting ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Send size={14} aria-hidden="true" />
            )}
            Yuborish
          </Button>
        </div>
      </form>

      {sorted.length === 0 ? (
        <p className="text-sm text-app-hint">Hali izoh yo&apos;q</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((comment) => (
            <li key={comment.id} className="text-sm">
              <p className="text-app-text">{comment.text}</p>
              <p className="mt-0.5 text-xs text-app-hint">{formatRelativeUz(comment.createdAt)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface DecisionFooterProps {
  stage: BloggerStage
  isPending: boolean
  errorMessage: string | null
  onApproveClick: () => void
  onRejectClick: () => void
  onClose: () => void
}

/**
 * Asosiy amallar — modal `shrink-0` footeri, hech qachon skroll ortida qolmaydi.
 *
 * `NEW` → tasdiqlash / rad etish (asosiy qaror). Boshqa bosqichlarda footer'da faqat
 * XAVFSIZ "Saqlash" (yopadi) tugmasi turadi — kartochkadagi o'zgarishlar (kitob, izoh)
 * allaqachon darhol saqlangani uchun bu tugma shunchaki kartochkani yopadi. Bloklash /
 * blokdan chiqarish bu yerda EMAS — ular yuqoridagi "⋯" menyuda, admin ularni bilmasdan
 * bosib yubormasligi uchun.
 */
function DecisionFooter({
  stage,
  isPending,
  errorMessage,
  onApproveClick,
  onRejectClick,
  onClose,
}: DecisionFooterProps) {
  return (
    <div className="shrink-0 border-t border-app-border bg-app-card p-4">
      {errorMessage && (
        <p className="mb-2 text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
      )}
      <div className="flex gap-2">
        {stage === BloggerStage.NEW ? (
          <>
            <Button className="flex-1" onClick={onApproveClick} disabled={isPending}>
              <CircleCheck size={16} aria-hidden="true" />
              Tasdiqlash
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={onRejectClick}
              disabled={isPending}
            >
              <XCircle size={16} aria-hidden="true" />
              Rad etish
            </Button>
          </>
        ) : (
          <Button variant="secondary" className="w-full" onClick={onClose} disabled={isPending}>
            <Check size={16} aria-hidden="true" />
            Saqlash
          </Button>
        )}
      </div>
    </div>
  )
}

/** Yuklanish paytidagi skeleton — header/scroll/footer bilan bir xil joylashuvda. */
function DetailSkeleton() {
  return (
    // `aria-busy` — skrinrider uchun: modal yuklanayotganda jim turmasin.
    <div className="flex h-full flex-col" aria-busy="true">
      <div className="shrink-0 border-b border-app-border p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-3 h-11 w-full" />
        <Skeleton className="mt-2 h-4 w-24" />
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-28" />
        <Skeleton className="h-32" />
      </div>
      <div className="shrink-0 border-t border-app-border p-4">
        <Skeleton className="h-11" />
      </div>
    </div>
  )
}

function DetailErrorState({
  error,
  onRetry,
  onClose,
}: {
  error: Error | null
  onRetry: () => void
  onClose: () => void
}) {
  const isServerError = error instanceof ApiError
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <CircleX size={28} className="text-app-hint" aria-hidden="true" />
      <p className="text-sm text-app-hint">
        {isServerError ? error.message : "Ma'lumotni yuklab bo'lmadi. Internetni tekshiring."}
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onClose}>
          Yopish
        </Button>
        <Button onClick={onRetry}>Qayta urinish</Button>
      </div>
    </div>
  )
}
